"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDownRight, ArrowUpRight, Info } from "lucide-react";
import { useId } from "react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/lib/components/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/lib/components/tooltip";
import { cn } from "@/lib/utils";
const accentColors: Record<"green" | "blue" | "purple", string> = {
  green: "#10b981",
  blue: "#3b82f6",
  purple: "#8b5cf6"
};
function Sparkline(t0) {
  const $ = _c(17);
  const {
    trend,
    color
  } = t0;
  const gradientId = useId();
  let t1;
  if ($[0] !== trend) {
    t1 = trend.map(_temp);
    $[0] = trend;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const chartData = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      top: 2,
      right: 0,
      left: 0,
      bottom: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] !== color) {
    t3 = <stop offset="5%" stopColor={color} stopOpacity={0.35} />;
    t4 = <stop offset="95%" stopColor={color} stopOpacity={0.02} />;
    $[3] = color;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== gradientId || $[7] !== t3 || $[8] !== t4) {
    t5 = <defs><linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">{t3}{t4}</linearGradient></defs>;
    $[6] = gradientId;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `url(#${gradientId})`;
  let t7;
  if ($[10] !== color || $[11] !== t6) {
    t7 = <Area type="monotone" dataKey="value" stroke={color} strokeWidth={1.5} fill={t6} isAnimationActive={false} dot={false} />;
    $[10] = color;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== chartData || $[14] !== t5 || $[15] !== t7) {
    t8 = <ResponsiveContainer width="100%" height="100%"><AreaChart data={chartData} margin={t2}>{t5}{t7}</AreaChart></ResponsiveContainer>;
    $[13] = chartData;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function _temp(value, index) {
  return {
    index,
    value
  };
}
export function MetricCard(t0) {
  const $ = _c(45);
  const {
    label,
    value,
    subtitle,
    icon,
    accent,
    tooltip,
    delta,
    deltaLabel: t1,
    trend,
    isLoading
  } = t0;
  const deltaLabel = t1 === undefined ? "vs previous period" : t1;
  const accentColor = accentColors[accent ?? "blue"];
  let t2;
  if ($[0] !== isLoading || $[1] !== trend) {
    t2 = !isLoading && trend && trend.length > 1 && trend.some(_temp2);
    $[0] = isLoading;
    $[1] = trend;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const showTrend = t2;
  let t3;
  if ($[3] !== delta || $[4] !== isLoading) {
    t3 = !isLoading && typeof delta === "number" && Number.isFinite(delta);
    $[3] = delta;
    $[4] = isLoading;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const showDelta = t3;
  let t4;
  if ($[6] !== label) {
    t4 = <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>;
    $[6] = label;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== label || $[9] !== tooltip) {
    t5 = tooltip ? <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><button type="button" aria-label={`More info about ${label}`} className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"><Info className="h-3 w-3" /></button></TooltipTrigger><TooltipContent side="top" className="max-w-xs text-center">{tooltip}</TooltipContent></Tooltip></TooltipProvider> : null;
    $[8] = label;
    $[9] = tooltip;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className="flex items-center gap-1.5">{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== delta || $[15] !== deltaLabel || $[16] !== isLoading || $[17] !== showDelta || $[18] !== value) {
    t7 = isLoading ? <Skeleton className="mt-2 h-7 w-24 sm:h-8" /> : <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1"><p className="text-xl font-semibold tabular-nums break-all sm:text-2xl">{value}</p>{showDelta ? <span title={deltaLabel} className={cn("inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums", delta >= 0 ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-red-500/10 text-red-600 dark:text-red-400")}>{delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(delta) >= 1000 ? ">999" : Math.abs(delta).toFixed(1)}%</span> : null}</div>;
    $[14] = delta;
    $[15] = deltaLabel;
    $[16] = isLoading;
    $[17] = showDelta;
    $[18] = value;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== isLoading || $[21] !== subtitle) {
    t8 = isLoading ? <Skeleton className="mt-2 h-3 w-32" /> : subtitle ? <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p> : null;
    $[20] = isLoading;
    $[21] = subtitle;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = <div className="min-w-0 flex-1">{t6}{t7}{t8}</div>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== accent || $[28] !== icon) {
    t10 = icon ? <div className={cn("hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border text-xs sm:inline-flex", accent === "green" && "border-emerald-500/30 bg-emerald-500/10 text-emerald-400", accent === "blue" && "border-sky-500/30 bg-sky-500/10 text-sky-400", accent === "purple" && "border-violet-500/30 bg-violet-500/10 text-violet-400")}>{icon}</div> : null;
    $[27] = accent;
    $[28] = icon;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <div className="flex items-start justify-between gap-3 p-4 pb-0 sm:p-5 sm:pb-0">{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const t12 = !showTrend && "h-4 sm:h-5";
  let t13;
  if ($[33] !== t12) {
    t13 = cn("h-10", t12);
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== accentColor || $[36] !== showTrend || $[37] !== trend) {
    t14 = showTrend ? <Sparkline trend={trend} color={accentColor} /> : null;
    $[35] = accentColor;
    $[36] = showTrend;
    $[37] = trend;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t13 || $[40] !== t14) {
    t15 = <div className={t13}>{t14}</div>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t11 || $[43] !== t15) {
    t16 = <div className="bg-card text-card-foreground relative flex flex-col justify-between overflow-hidden rounded-xl border border-border/60 shadow-sm">{t11}{t15}</div>;
    $[42] = t11;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
}
function _temp2(v) {
  return v !== 0;
}